/**
 * المرحلة 1.3 (P29) — «طلب الانضمام»: a new doctor's documents and the decision.
 *
 * A doctor who registered waits for approval and cannot sign in; they reach
 * this page with the applicant token their registration (or a refused
 * sign-in) handed them, or with their own session once active (to renew a
 * document). They pick their specialty, send each document the
 * doctorOnboarding policy asks for — the scan, its number and its dates —
 * and submit. A rejection comes back here with the reviewer's reason; the
 * doctor fixes what it names and submits again. Approval opens the account.
 *
 * «تفعيل» in the Super Admin's list still activates a doctor without any of
 * this: the application sits beside it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, DateInput, EmptyState, Field, Icon, Input, Modal, Panel, Select, Spinner } from '../../components/common';
import { applicationApi, categoryApi } from '../../api/endpoints';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { categoryLabel, mergeCategories } from '../../domain/catalog';
import { compressImage, readFileAsDataUri } from '../../domain/files';
import { DOC_KINDS } from '../../domain/discovery';
import { formatDate } from '../../domain/locale';
import { useI18n } from '../../i18n/I18nProvider';
import { LanguageSwitch } from '../../i18n/LanguageSwitch';
import { clearApplicant, readApplicant } from './applicant';

const KIND_ICONS = { licence: 'file-badge', syndicate: 'badge-check', id: 'id-card', specialty: 'graduation-cap' };

export function DoctorApplicationPage() {
  const { t, lang } = useI18n();
  const { isAuthenticated, role } = useAuth();
  const { success, error: toastError } = useNotifications();
  // An active doctor uses their session; a waiting one their applicant token.
  const token = isAuthenticated && role === 'doctor' ? null : readApplicant();
  const usable = Boolean(token) || (isAuthenticated && role === 'doctor');

  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!usable) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    applicationApi
      .mine(token)
      .then((data) => setState({ loading: false, error: null, data }))
      .catch((error) => {
        if (error.status === 401) clearApplicant();
        setState({ loading: false, error, data: null });
      });
  }, [token, usable]);

  useEffect(() => {
    load();
    categoryApi.list().then((rows) => setCategories(mergeCategories(rows))).catch(() => setCategories(mergeCategories([])));
  }, [load]);

  const shell = (children) => (
    <div className="auth-screen app-backdrop flex min-h-screen justify-center p-4" data-testid="apply-page">
      <div className="auth-screen__lang">
        <LanguageSwitch />
      </div>
      <div className="w-full max-w-3xl space-y-5 py-10">{children}</div>
    </div>
  );

  if (!usable || (state.error && state.error.status === 401)) {
    return shell(
      <Panel>
        <EmptyState
          icon="lock"
          title={t('apply.signInTitle')}
          message={t('apply.signInMessage')}
          action={<Link to="/login" className="btn-primary">{t('apply.signIn')}</Link>}
        />
      </Panel>,
    );
  }
  if (state.loading && !state.data) return shell(<Spinner label={t('apply.loading')} />);
  if (state.error) return shell(<Panel><EmptyState icon="alert-triangle" title={t('apply.failed')} message={state.error.message} action={<Button onClick={load}>{t('common.retry')}</Button>} /></Panel>);

  const { account, application, documents, required, missing } = state.data;
  const locked = application.status === 'submitted' || application.status === 'approved';
  const byKind = new Map(documents.map((d) => [d.kind, d]));
  const kinds = DOC_KINDS.filter((k) => required.includes(k) || byKind.has(k));

  const pickCategory = async (categoryId) => {
    setBusy(true);
    try {
      const next = await applicationApi.setCategory(token, categoryId);
      setState((s) => ({ ...s, data: { ...s.data, ...next } }));
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const next = await applicationApi.submit(token);
      setState((s) => ({ ...s, data: { ...s.data, ...next } }));
      success(t('apply.submitted'));
    } catch (err) {
      const code = err.body?.code;
      toastError(code === 'DOCUMENTS_MISSING' ? t('apply.missingDocs', { kinds: (err.body.kinds || []).map((k) => t(`apply.kind.${k}`)).join(t('proProfile.joiner')) }) : code === 'CATEGORY_MISSING' ? t('apply.pickSpecialtyFirst') : err.message);
    } finally {
      setBusy(false);
    }
  };

  const view = async (doc) => {
    try {
      const scan = await applicationApi.image(application.id, doc.id, token);
      setViewing(scan);
    } catch (err) {
      toastError(err.message);
    }
  };

  return shell(
    <>
      <Panel>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-black text-white">
              <Icon name="file-badge" size={22} className="text-cyan-400" />
              {t('apply.title')}
            </h1>
            <p className="mt-1 text-sm text-slate-400">{t('apply.hello', { name: account.name })}</p>
          </div>
          <span data-testid="apply-status">
            <Badge className={STATUS_TONE[application.status]}>{t(`apply.status.${application.status}`)}</Badge>
          </span>
        </div>

        {application.status === 'rejected' && application.decisionReason && (
          <p className="reason-note mt-4" data-testid="apply-reason">
            <Icon name="message-square-text" size={14} className="mt-0.5 shrink-0" />
            <span>
              <strong>{t('apply.reasonLabel')}</strong> {application.decisionReason}
            </span>
          </p>
        )}
        {application.status === 'submitted' && (
          <p className="info-note mt-4">
            <Icon name="hourglass" size={14} className="mt-0.5 shrink-0" />
            <span>{t('apply.waiting')}</span>
          </p>
        )}
        {application.status === 'approved' && (
          <p className="info-note mt-4">
            <Icon name="check-circle-2" size={14} className="mt-0.5 shrink-0" />
            <span>
              {t('apply.approved')}{' '}
              {!isAuthenticated && (
                <Link to="/login" className="font-bold underline" onClick={clearApplicant}>
                  {t('apply.signIn')}
                </Link>
              )}
            </span>
          </p>
        )}
      </Panel>

      <Panel>
        <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-white">
          <Icon name="stethoscope" size={18} className="text-emerald-400" />
          {t('apply.specialty')}
        </h2>
        <Select
          value={application.categoryId || ''}
          disabled={locked || busy}
          onChange={(e) => pickCategory(e.target.value)}
          aria-label={t('apply.specialty')}
          data-testid="apply-specialty"
        >
          <option value="">{t('apply.pickSpecialty')}</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {categoryLabel(c, lang)}
            </option>
          ))}
        </Select>
      </Panel>

      <Panel>
        <h2 className="mb-1 flex items-center gap-2 text-base font-bold text-white">
          <Icon name="file-check-2" size={18} className="text-cyan-400" />
          {t('apply.documents')}
        </h2>
        <p className="mb-4 text-xs text-slate-400">{t('apply.documentsHint')}</p>
        <div className="space-y-2">
          {kinds.map((kind) => {
            const doc = byKind.get(kind);
            const status = doc ? doc.status : 'missing';
            return (
              <div key={kind} className={`doc-row doc-row--${status}`} data-testid={`doc-${kind}`}>
                <span className="doc-row__icon">
                  <Icon name={KIND_ICONS[kind] || 'file-text'} size={18} />
                </span>
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-white">
                    {t(`apply.kind.${kind}`)}
                    <span className={`doc-status doc-status--${status}`}>{t(`apply.docStatus.${status}`)}</span>
                  </p>
                  {doc && (
                    <p className="mt-0.5 text-xs text-slate-400">
                      <span dir="ltr">{doc.number}</span>
                      {doc.expiresOn ? ` · ${t('apply.expires', { date: formatDate(doc.expiresOn) })}` : ''}
                    </p>
                  )}
                  {doc?.status === 'rejected' && doc.reason && <p className="mt-1 text-xs text-rose-300">{doc.reason}</p>}
                </div>
                <div className="doc-row__actions">
                  {doc && (
                    <Button variant="subtle" icon="eye" onClick={() => view(doc)} aria-label={t('apply.view')}>
                      <span className="hidden sm:inline">{t('apply.view')}</span>
                    </Button>
                  )}
                  {application.status !== 'submitted' && status !== 'accepted' && (
                    <Button variant={doc ? 'ghost' : 'primary'} icon="upload" onClick={() => setUploading(kind)} data-testid={`upload-${kind}`}>
                      {doc ? t('apply.replace') : t('apply.upload')}
                    </Button>
                  )}
                  {application.status === 'approved' && status === 'accepted' && (
                    <Button variant="ghost" icon="refresh-cw" onClick={() => setUploading(kind)}>
                      {t('apply.renew')}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!locked && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-400">
              {missing.length ? t('apply.stillMissing', { n: missing.length }) : t('apply.ready')}
            </p>
            <Button icon="send" onClick={submit} loading={busy} disabled={missing.length > 0 || !application.categoryId} data-testid="apply-submit">
              {application.status === 'rejected' ? t('apply.resubmit') : t('apply.submit')}
            </Button>
          </div>
        )}
      </Panel>

      <UploadDialog
        kind={uploading}
        token={token}
        onClose={() => setUploading(null)}
        onSaved={(next) => {
          setUploading(null);
          setState((s) => ({ ...s, data: { ...s.data, ...next } }));
          success(t('apply.uploaded'));
        }}
      />

      <Modal open={Boolean(viewing)} onClose={() => setViewing(null)} title={viewing ? t(`apply.kind.${viewing.kind}`) : ''} icon="file-badge" size="lg">
        {viewing && (String(viewing.image).startsWith('data:application/pdf') ? (
          <iframe src={viewing.image} title={t(`apply.kind.${viewing.kind}`)} className="h-[65vh] w-full rounded-lg" />
        ) : (
          <img src={viewing.image} alt={t(`apply.kind.${viewing.kind}`)} className="doc-scan" />
        ))}
      </Modal>
    </>,
  );
}

const STATUS_TONE = {
  draft: 'border-slate-500/30 bg-slate-500/10 text-slate-300',
  submitted: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  approved: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  rejected: 'border-rose-500/30 bg-rose-500/10 text-rose-300',
};

/** One document: the scan, its number, its dates. */
function UploadDialog({ kind, token, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ number: '', issuedOn: '', expiresOn: '', image: null, name: '' });
  const fileInput = useRef(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm({ number: '', issuedOn: '', expiresOn: '', image: null, name: '' });
    setError(null);
  }, [kind]);

  if (!kind) return null;

  const pickFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const image = file.type === 'application/pdf' ? await readFileAsDataUri(file) : await compressImage(file, { maxWidth: 1800, quality: 0.8 });
      setForm((f) => ({ ...f, image, name: file.name }));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const next = await applicationApi.upload(token, { kind, number: form.number, issuedOn: form.issuedOn || undefined, expiresOn: form.expiresOn, image: form.image });
      onSaved(next);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={Boolean(kind)}
      onClose={onClose}
      title={t('apply.uploadTitle', { kind: t(`apply.kind.${kind}`) })}
      icon="upload"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button icon="save" onClick={save} loading={saving} disabled={!form.image || !form.number.trim() || !form.expiresOn} data-testid="upload-save">
            {t('apply.send')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('apply.scan')} hint={t('apply.scanHint')} required>
          {/* The browser's own picker reads «Choose File» in its own language;
              a button of ours says it in the reader's (as the insurance card's). */}
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf"
              onChange={pickFile}
              className="hidden"
              data-testid="upload-file"
            />
            <Button variant="ghost" icon="upload" onClick={() => fileInput.current && fileInput.current.click()} data-testid="upload-pick">
              {form.name ? t('apply.otherFile') : t('apply.chooseFile')}
            </Button>
            {form.name && (
              <span className="min-w-0 truncate text-xs text-emerald-300" dir="auto">
                {form.name}
              </span>
            )}
          </div>
        </Field>
        <Field label={t('apply.number')} required>
          <Input dir="ltr" value={form.number} maxLength={40} onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))} data-testid="upload-number" />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('apply.issuedOn')}>
            <DateInput value={form.issuedOn} onChange={(e) => setForm((f) => ({ ...f, issuedOn: e.target.value }))} />
          </Field>
          <Field label={t('apply.expiresOn')} required>
            <DateInput value={form.expiresOn} onChange={(e) => setForm((f) => ({ ...f, expiresOn: e.target.value }))} data-testid="upload-expires" />
          </Field>
        </div>
        {error && (
          <p className="reason-note">
            <Icon name="alert-triangle" size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </p>
        )}
      </div>
    </Modal>
  );
}

export default DoctorApplicationPage;
